import React, { useState, useCallback } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase';
import { PhotoIcon, XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

interface ImageUploadDropzoneProps {
  photos: Array<{ url: string; isPrimary?: boolean }>;
  onPhotosChange: (photos: Array<{ url: string; isPrimary?: boolean }>) => void;
  vehicleId?: string;
}

const ImageUploadDropzone: React.FC<ImageUploadDropzoneProps> = ({
  photos,
  onPhotosChange,
  vehicleId
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>('');

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const uploadImage = async (file: File): Promise<string> => {
    const timestamp = Date.now();
    const fileName = `vehicles/${vehicleId || 'temp'}/${timestamp}_${file.name}`;
    const storageRef = ref(storage, fileName);

    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    return url;
  };

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );

    if (files.length === 0) {
      alert('Csak képfájlokat tölthetsz fel!');
      return;
    }

    setUploading(true);
    const newPhotos = [...photos];

    for (let i = 0; i < files.length; i++) {
      setUploadProgress(`Feltöltés ${i + 1}/${files.length}...`);
      try {
        const url = await uploadImage(files[i]);
        newPhotos.push({ 
          url, 
          isPrimary: newPhotos.length === 0 // Az első kép legyen primary
        });
      } catch (error) {
        console.error('Feltöltési hiba:', error);
        alert(`Hiba a ${files[i].name} feltöltése során`);
      }
    }

    onPhotosChange(newPhotos);
    setUploading(false);
    setUploadProgress('');
  }, [photos, onPhotosChange, vehicleId]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter(file => 
      file.type.startsWith('image/')
    );

    if (files.length === 0) return;

    setUploading(true);
    const newPhotos = [...photos];

    for (let i = 0; i < files.length; i++) {
      setUploadProgress(`Feltöltés ${i + 1}/${files.length}...`);
      try {
        const url = await uploadImage(files[i]);
        newPhotos.push({ 
          url, 
          isPrimary: newPhotos.length === 0
        });
      } catch (error) {
        console.error('Feltöltési hiba:', error);
        alert(`Hiba a ${files[i].name} feltöltése során`);
      }
    }

    onPhotosChange(newPhotos);
    setUploading(false);
    setUploadProgress('');
    e.target.value = ''; // Reset input
  };

  const removePhoto = (index: number) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    // Ha az eltávolított kép volt a primary, az első legyen az új primary
    if (photos[index].isPrimary && newPhotos.length > 0) {
      newPhotos[0].isPrimary = true;
    }
    onPhotosChange(newPhotos);
  };

  const setPrimaryPhoto = (index: number) => {
    const newPhotos = photos.map((photo, i) => ({
      ...photo,
      isPrimary: i === index
    }));
    onPhotosChange(newPhotos);
  };

  return (
    <div className="space-y-4">
      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center transition-colors
          ${isDragging 
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
            : 'border-gray-300 dark:border-gray-600 hover:border-primary-400'
          }
          ${uploading ? 'opacity-50 pointer-events-none' : ''}
        `}
      >
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={uploading}
        />
        
        <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          {uploading ? uploadProgress : 'Húzd ide a képeket vagy kattints a tallózáshoz'}
        </p>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
          PNG, JPG, GIF max. 10MB
        </p>
      </div>

      {/* Képek listája */}
      {photos.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {photos.map((photo, index) => (
            <div
              key={index}
              className="relative group rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-700"
            >
              <img
                src={photo.url}
                alt={`Autó kép ${index + 1}`}
                className="w-full h-32 object-cover"
              />
              
              {/* Primary badge */}
              {photo.isPrimary && (
                <div className="absolute top-2 left-2 bg-primary-600 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                  <CheckCircleIcon className="h-3 w-3" />
                  Fő kép
                </div>
              )}

              {/* Akciók */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                {!photo.isPrimary && (
                  <button
                    type="button"
                    onClick={() => setPrimaryPhoto(index)}
                    className="bg-primary-600 text-white px-3 py-1 rounded text-xs hover:bg-primary-700"
                  >
                    Fő kép
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => removePhoto(index)}
                  className="bg-red-600 text-white p-2 rounded hover:bg-red-700"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {photos.length > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {photos.length} kép feltöltve. A fő kép jelenik meg a weboldalon.
        </p>
      )}
    </div>
  );
};

export default ImageUploadDropzone;
